import {
  calculateEnergyCarbonFootprint,
  calculateInherentEnergyCarbonFootprint,
  calculateInherentFirstYearEnergyCarbonFootprint,
  calculateTotalEnergyCarbonFootprint,
  getEnergyResults,
} from '../../../calculators/carbon-footprint/energy-carbon-footprint-calculator'
import {
  getFixedResults,
  calculateTotalFixedCarbonFootprint,
  calculateInherentFixedCarbonFootprint,
} from '../../../calculators/carbon-footprint/fixed-carbon-footprint-calculator'
import { CalculatedVersion } from '../../../calculators/use-calculator'
import { EmissionModel } from '../../../emission-coefficients/emission-coefficients'
import { EmissionType } from '../../../generated/graphql'
import {
  PurchasedEnergyResultSubCategory,
  purchasedEnergySubCategories,
} from '../../../provider/result-categories'
import { reduceToSumOfItems } from '../../../util/array-utility'

type GetEnergyResults = (
  subCategories: PurchasedEnergyResultSubCategory[],
) => (number | null)[]
type GetFixedResults = (emissionType?: EmissionType) => (number | null)[]

type UseCarbonFootprintResults = {
  getFirstYearResults: GetEnergyResults
  getInherentFirstYearResults: GetEnergyResults
  getCumulativeResults: GetEnergyResults
  getInherentCumulativeResults: GetEnergyResults
  getLifetimeFootprints: GetFixedResults
  getInherentLifetimeFootprints: GetFixedResults
}

export function useCarbonFootprintResults(
  versions: CalculatedVersion[],
  emissionModel: EmissionModel,
  selectedBuildingIds?: string[],
): UseCarbonFootprintResults {
  const getFirstYearResults = (
    subCategories: PurchasedEnergyResultSubCategory[],
  ) =>
    getEnergyResults(
      versions,
      subCategories,
      (energies, { buildingYear }) =>
        calculateEnergyCarbonFootprint(energies, buildingYear),
      emissionModel,
      selectedBuildingIds,
    )

  const getInherentFirstYearResults = (
    subCategories: PurchasedEnergyResultSubCategory[],
  ) =>
    getEnergyResults(
      versions,
      subCategories,
      calculateInherentFirstYearEnergyCarbonFootprint,
      emissionModel,
      selectedBuildingIds,
    )

  const getCumulativeResults = (
    subCategories: PurchasedEnergyResultSubCategory[],
  ) =>
    getEnergyResults(
      versions,
      subCategories,
      calculateTotalEnergyCarbonFootprint,
      emissionModel,
      selectedBuildingIds,
    )

  const getInherentCumulativeResults = (
    subCategories: PurchasedEnergyResultSubCategory[],
  ) =>
    getEnergyResults(
      versions,
      subCategories,
      calculateInherentEnergyCarbonFootprint,
      emissionModel,
      selectedBuildingIds,
    )
  const getTotalFixedResults = (emissionType?: EmissionType) =>
    getFixedResults(
      versions,
      calculateTotalFixedCarbonFootprint,
      emissionModel,
      emissionType,
      selectedBuildingIds,
    )

  const getInherentTotalFixedResults = (emissionType?: EmissionType) =>
    getFixedResults(
      versions,
      calculateInherentFixedCarbonFootprint,
      emissionModel,
      emissionType,
      selectedBuildingIds,
    )

  const getLifetimeFootprints = (emissionType?: EmissionType) => {
    if (!emissionType) {
      return [
        getCumulativeResults(purchasedEnergySubCategories),
        getTotalFixedResults(),
      ].reduce(reduceToSumOfItems, [])
    }

    return emissionType === EmissionType.Energy
      ? getCumulativeResults(purchasedEnergySubCategories)
      : getTotalFixedResults(emissionType)
  }

  const getInherentLifetimeFootprints = (emissionType?: EmissionType) => {
    if (!emissionType) {
      return [
        getInherentCumulativeResults(purchasedEnergySubCategories),
        getInherentTotalFixedResults(),
      ].reduce(reduceToSumOfItems, [])
    }

    return emissionType === EmissionType.Energy
      ? getInherentCumulativeResults(purchasedEnergySubCategories)
      : getInherentTotalFixedResults(emissionType)
  }

  return {
    getFirstYearResults,
    getInherentFirstYearResults,
    getCumulativeResults,
    getInherentCumulativeResults,
    getLifetimeFootprints,
    getInherentLifetimeFootprints,
  }
}
